import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Bookmark, Check, CheckCircle2, ChevronLeft, ChevronRight, Clock, Wind } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, resolveImage, difficultyClass, localDate, moodLabel } from "@/lib/api";
import { celebrateBadges } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { RichText } from "@/components/RichText";
import { VideoEmbed } from "@/components/VideoEmbed";
import { BreathingExercise } from "@/components/BreathingExercise";
import { ReflectionPrompt } from "@/components/ReflectionPrompt";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function TutorialReader() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tutorial, setTutorial] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [progress, setProgress] = useState(null);
  const [idx, setIdx] = useState(0);
  const [lastSession, setLastSession] = useState(null);
  const deepLink = useRef({ step: searchParams.get("step"), focus: searchParams.get("focus") });

  useEffect(() => {
    setTutorial(null); setProgress(null); setIdx(0);
    api.get(`/tutorials/${id}`).then((r) => setTutorial(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!user || !tutorial) return;
    api.get(`/progress/${id}`).then((r) => {
      setProgress(r.data);
      const { step, focus } = deepLink.current;
      const wanted = step != null && !Number.isNaN(Number(step)) ? Number(step) : (r.data.last_step_index || 0);
      setIdx(Math.min(wanted, Math.max(tutorial.steps.length - 1, 0)));
      if (focus === "breathing") {
        deepLink.current = {};
        setSearchParams({}, { replace: true });
        setTimeout(() => document.getElementById("breathing-exercise")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tutorial, id]);

  const steps = tutorial?.steps || [];
  const step = steps[idx];
  const completed = useMemo(() => new Set(progress?.completed_steps || []), [progress]);

  const save = useCallback(async (patch) => {
    if (!user) return null;
    try {
      const r = await api.put(`/progress/${id}`, patch);
      setProgress(r.data);
      return r.data;
    } catch (e) { toast.error(errorMessage(e)); return null; }
  }, [user, id]);

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(i, steps.length - 1));
    setIdx(clamped);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (user) save({ last_step_index: clamped });
  };

  const markComplete = async (stepId, silent = false) => {
    if (!user) { toast("Sign in to save your progress"); return; }
    if (completed.has(stepId)) return;
    const next = [...completed, stepId];
    const all = steps.every((s) => next.includes(s.id));
    const r = await save({ completed_steps: next, is_completed: all });
    if (r && !silent) toast.success(all ? "Tutorial completed — well done!" : "Step marked complete");
    if (r && all && silent) toast.success("Tutorial completed — well done!");
  };

  const logSession = async (stepId, { seconds, rounds, completed, mood_before, mood_after }) => {
    if (!user) return;
    try {
      const r = await api.post("/practice", { tutorial_id: id, step_id: stepId, seconds, rounds, completed, local_date: localDate(), mood_before: mood_before || null, mood_after: mood_after || null });
      setLastSession(r.data);
      toast.success(`${Math.round(seconds / 60)} min added to your practice log`);
      celebrateBadges(r.data.new_badges);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const askCompanion = ({ minutes, rounds, mood_before, mood_after }) => {
    const mood = mood_before && mood_after ? `, mood went ${moodLabel(mood_before).toLowerCase()} → ${moodLabel(mood_after).toLowerCase()}` : "";
    const line = `I just finished ${minutes} min of ${tutorial.title} (${rounds} round${rounds === 1 ? "" : "s"})${mood}. Help me reflect on how that felt.`;
    navigate(`/companion?prefill=${encodeURIComponent(line)}`);
  };

  const toggleBookmark = async () => {
    if (!user) { toast("Sign in to save bookmarks"); navigate("/login"); return; }
    try {
      const r = await api.post(`/progress/${id}/bookmark`);
      setProgress(r.data);
      toast.success(r.data.is_bookmarked ? "Added to My Learning" : "Bookmark removed");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="tutorial-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-900">Tutorial not found</h1>
        <Button asChild className="mt-6 rounded-full"><Link to="/">Back to library</Link></Button>
      </div>
    );
  }
  if (!tutorial) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 space-y-4"><Skeleton className="h-8 w-80" /><Skeleton className="h-64 w-full rounded-3xl" /></div>;
  }

  const pct = steps.length ? Math.round((completed.size / steps.length) * 100) : 0;
  const isLast = idx === steps.length - 1;

  return (
    <div data-testid="tutorial-reader">
      <div className="h-1.5 w-full bg-slate-100 sticky top-16 z-30">
        <div className="h-full bg-indigo-600 transition-all duration-500 ease-out" style={{ width: `${pct}%` }} data-testid="reader-progress-bar" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div className="space-y-3">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700" data-testid="back-to-library-link"><ArrowLeft className="w-4 h-4" /> Library</Link>
            <p className="eyebrow">{tutorial.category_name}</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="tutorial-title">{tutorial.title}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${difficultyClass[tutorial.difficulty]}`}>{tutorial.difficulty}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{tutorial.estimated_minutes} min</span>
              <span>{steps.length} steps</span>
              {tutorial.status === "draft" && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="draft-badge">Draft preview</span>}
            </div>
          </div>
          <Button variant={progress?.is_bookmarked ? "default" : "outline"} onClick={toggleBookmark}
            className={`rounded-full ${progress?.is_bookmarked ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`} data-testid="reader-bookmark-button">
            <Bookmark className={`w-4 h-4 ${progress?.is_bookmarked ? "fill-current" : ""}`} /> {progress?.is_bookmarked ? "Bookmarked" : "Bookmark"}
          </Button>
        </div>

        {!user && (
          <div className="mb-8 rounded-2xl bg-indigo-50 border border-indigo-100 px-5 py-4 text-sm text-indigo-900 flex flex-wrap items-center justify-between gap-3" data-testid="signin-banner">
            <span>You can read everything without an account. Sign in to save your progress and bookmarks.</span>
            <Button size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => navigate("/login")} data-testid="banner-signin-button">Sign in</Button>
          </div>
        )}

        {steps.length === 0 ? (
          <p className="text-slate-500" data-testid="no-steps">This tutorial has no steps yet.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <aside className="lg:col-span-4 xl:col-span-3 lg:sticky lg:top-28" data-testid="step-list">
              <ol className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
                {steps.map((s, i) => {
                  const done = completed.has(s.id);
                  const current = i === idx;
                  return (
                    <li key={s.id} className="shrink-0 lg:shrink">
                      <button type="button" onClick={() => goTo(i)} data-testid={`step-list-item-${i}`}
                        className={`w-full text-left flex items-center gap-3 rounded-2xl px-4 py-3 border transition-colors ${current ? "bg-white border-indigo-300 shadow-sm" : "border-transparent hover:bg-white hover:border-slate-200"}`}>
                        <span className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs font-bold ${done ? "bg-emerald-500 text-white" : current ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                          {done ? <Check className="w-4 h-4" /> : i + 1}
                        </span>
                        <span className={`text-sm font-medium truncate max-w-[180px] lg:max-w-none ${current ? "text-slate-900" : "text-slate-600"}`}>{s.title}</span>
                        {s.type === "breathing" && <Wind className="w-4 h-4 text-indigo-500 ml-auto shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </aside>

            <main className="lg:col-span-8 xl:col-span-9 space-y-8" key={step.id}>
              <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-10 space-y-6 rise" data-testid="step-content">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Step {idx + 1} of {steps.length}</p>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-2" data-testid="step-title">{step.title}</h2>
                </div>
                {step.image_url && (
                  <img src={resolveImage(step.image_url)} alt={step.title} className="w-full max-h-[80vh] object-cover rounded-2xl border border-slate-200" data-testid="step-image" />
                )}
                {step.video_url && <VideoEmbed url={step.video_url} />}
                <RichText text={step.body} />
                {step.type === "breathing" && (
                  <div id="breathing-exercise" className="scroll-mt-24 space-y-6">
                    <BreathingExercise config={step.breathing} alreadyCompleted={completed.has(step.id)} onComplete={() => markComplete(step.id, true)} onSessionEnd={(info) => logSession(step.id, info)} onAskCompanion={askCompanion} />
                    <ReflectionPrompt session={lastSession} onDone={() => setLastSession(null)} />
                  </div>
                )}
                {step.type !== "breathing" && (
                  <div className="pt-2">
                    {completed.has(step.id) ? (
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="step-completed-indicator"><CheckCircle2 className="w-4 h-4" /> Completed</span>
                    ) : (
                      <Button variant="outline" onClick={() => markComplete(step.id)} className="rounded-full" data-testid="mark-step-complete-button"><Check className="w-4 h-4" /> Mark complete</Button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button variant="outline" className="rounded-full" disabled={idx === 0} onClick={() => goTo(idx - 1)} data-testid="tutorial-step-prev-button"><ChevronLeft className="w-4 h-4" /> Previous</Button>
                {isLast ? (
                  <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={async () => { await markComplete(step.id, true); navigate(user ? "/my-learning" : "/"); }} data-testid="tutorial-finish-button">
                    Finish <CheckCircle2 className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => goTo(idx + 1)} data-testid="tutorial-step-next-button">Next <ChevronRight className="w-4 h-4" /></Button>
                )}
              </div>
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
